import { useMemo, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import { useEnquiry } from "../context/EnquiryContext";
import { CATEGORIES } from "../data/content";

const FILTERS = ["All", ...CATEGORIES.map((c) => c.name)];

export default function Products() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const { openEnquiry } = useEnquiry();

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATEGORIES.map((cat) => {
      if (filter !== "All" && cat.name !== filter) return { ...cat, items: [] };
      if (!q) return cat;
      const catMatch = cat.name.toLowerCase().includes(q);
      return {
        ...cat,
        items: catMatch ? cat.items : cat.items.filter((i) => i.toLowerCase().includes(q)),
      };
    }).filter((cat) => cat.items.length > 0 || (!query && filter === cat.name));
  }, [query, filter]);

  const totalItems = visible.reduce((n, c) => n + c.items.length, 0);

  return (
    <main data-testid="products-page">
      <section className="pt-32 sm:pt-44 pb-14 sm:pb-20 px-5 sm:px-8 lg:px-12 border-b border-ink/15">
        <FadeUp immediate y={12}>
          <Overline className="mb-8">Product Range</Overline>
        </FadeUp>
        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-[13vw] sm:text-8xl">
          <MaskedLine immediate delay={0.15}>What We</MaskedLine>
          <MaskedLine immediate delay={0.3}>Supply</MaskedLine>
        </h1>
        <FadeUp immediate delay={0.45} className="mt-8 max-w-xl">
          <p className="text-base sm:text-lg text-ink/65 leading-relaxed">
            A broad range of hardware, tools and industrial supplies for professional
            requirements. Find what you need and send us an enquiry.
          </p>
        </FadeUp>
      </section>

      <section className="sticky top-[57px] sm:top-[61px] z-30 bg-paper border-b border-ink/15">
        <div className="px-5 sm:px-8 lg:px-12 py-5 flex flex-col gap-4">
          <label
            htmlFor="product-search"
            className="flex items-center gap-3 border border-ink/25 bg-white px-4 py-3 focus-within:border-ink transition-colors"
          >
            <Search size={16} className="text-ink/40 shrink-0" />
            <input
              id="product-search"
              data-testid="product-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What are you looking for?"
              className="w-full bg-transparent text-sm placeholder:text-ink/40 focus:outline-none"
            />
          </label>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Product categories">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`shrink-0 font-mono text-[10px] sm:text-[11px] tracking-[0.15em] uppercase px-4 py-2 border transition-colors ${
                  filter === f
                    ? "bg-ink text-paper border-ink"
                    : "border-ink/25 text-ink/60 hover:border-ink hover:text-ink"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-8 lg:px-12 py-12 sm:py-16 min-h-[40vh]">
        {totalItems === 0 ? (
          <div data-testid="products-empty-state" className="py-20">
            <p className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl text-ink/70">
              No matches for &ldquo;{query}&rdquo;.
            </p>
            <p className="mt-4 text-sm text-ink/55 max-w-md leading-relaxed">
              We supply far more than what is listed. Send us your requirement and we will source
              it for you.
            </p>
            <button
              data-testid="products-empty-enquire"
              onClick={() => openEnquiry(query)}
              className="mt-8 inline-flex items-center gap-2 bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 hover:bg-accent hover:text-ink transition-colors"
            >
              Enquire Anyway <ArrowUpRight size={14} />
            </button>
          </div>
        ) : (
          visible.map((cat) => (
            <div key={cat.id} className="mb-14" data-testid={`products-group-${cat.id}`}>
              {cat.img && (
                <div className="mb-6 overflow-hidden border border-ink/15 h-44 sm:h-56">
                  <img
                    src={cat.img}
                    alt={`${cat.name} supplied by Jagdamba Enterprises`}
                    className="h-full w-full object-cover grayscale contrast-125 hover:scale-[1.02] transition-transform duration-[1.2s] ease-out"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="flex items-baseline gap-5 border-b-2 border-ink pb-4 mb-2">
                <span className="font-mono text-xs text-ink/40">{cat.id}</span>
                <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-4xl">
                  {cat.name}
                </h2>
                <span className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-ink/40">
                  {cat.items.length} items
                </span>
              </div>
              <ul>
                {cat.items.map((item) => (
                  <li key={item} className="border-b border-ink/10">
                    <button
                      data-testid={`enquire-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => openEnquiry(item)}
                      className="w-full flex items-center justify-between gap-4 py-4 text-left group"
                    >
                      <span className="text-base sm:text-lg text-ink/80 group-hover:text-ink transition-colors">
                        {item}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.15em] uppercase flex items-center gap-1 text-ink/40 group-hover:text-ink group-hover:gap-2 transition-all">
                        Enquire <ArrowUpRight size={13} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {cat.note && (
                <p className="mt-4 font-mono text-[11px] tracking-[0.1em] uppercase text-ink/45">
                  {cat.note}
                </p>
              )}
            </div>
          ))
        )}
      </section>

      <section className="bg-ink text-paper px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-3xl sm:text-5xl max-w-2xl">
            Can&apos;t find what you need? We&apos;ll source it.
          </h2>
          <button
            data-testid="products-bottom-cta"
            onClick={() => openEnquiry("")}
            className="inline-flex items-center gap-2 bg-accent text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-paper transition-colors self-start"
          >
            Send Your Requirement <ArrowUpRight size={14} />
          </button>
        </div>
      </section>
    </main>
  );
}
